import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function OrderConfirm() {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        api.get(`/orders/${orderNumber}`)
            .then((r) => setOrder(r.data))
            .catch(() => setErr(true));
    }, [orderNumber]);

    if (err)
        return (
            <div
                className="max-w-[900px] mx-auto px-6 md:px-12 py-40 text-center"
                style={{ color: "var(--vl-muted)" }}
            >
                Order not found.
            </div>
        );
    if (!order)
        return (
            <div
                className="max-w-[900px] mx-auto px-6 md:px-12 py-40 text-center"
                style={{ color: "var(--vl-muted)" }}
            >
                Loading your order…
            </div>
        );

    return (
        <div
            className="max-w-[900px] mx-auto px-6 md:px-12 pt-16 pb-32"
            data-testid="order-confirm-page"
        >
            <div
                className="flex items-center gap-3 mb-5"
                style={{ color: "var(--vl-rose)" }}
            >
                <CheckCircle2 size={22} strokeWidth={1.25} />
                <span className="overline">Order confirmed</span>
            </div>
            <h1
                className="font-serif text-5xl sm:text-6xl tracking-tight"
                style={{ color: "var(--vl-text)" }}
            >
                Thank you,{" "}
                <em
                    style={{
                        color: "var(--vl-rose)",
                        fontStyle: "italic",
                    }}
                >
                    {order.full_name.split(" ")[0]}.
                </em>
            </h1>
            <p
                className="mt-5 max-w-xl"
                style={{
                    color: "var(--vl-muted)",
                    lineHeight: 1.8,
                }}
            >
                A private confirmation has been sent to{" "}
                <span style={{ color: "var(--vl-text)" }}>{order.email}</span>
                . Your order will arrive in unbranded packaging. Return label
                reads <em>V. Atelier</em>.
            </p>

            <div
                className="mt-10 p-8"
                style={{
                    background: "var(--vl-surface)",
                    border: "1px solid var(--vl-border)",
                }}
            >
                <div className="flex justify-between flex-wrap gap-4 mb-6">
                    <div>
                        <div className="overline">Order number</div>
                        <div
                            className="font-serif text-2xl mt-1"
                            style={{ color: "var(--vl-text)" }}
                            data-testid="order-number"
                        >
                            {order.order_number}
                        </div>
                    </div>
                    <div>
                        <div className="overline">Status</div>
                        <div
                            className="mt-1"
                            style={{ color: "var(--vl-rose)" }}
                        >
                            {order.status.toUpperCase()}
                        </div>
                    </div>
                    <div>
                        <div className="overline">Payment</div>
                        <div
                            className="mt-1"
                            style={{ color: "var(--vl-text)" }}
                        >
                            {order.payment_method === "cod"
                                ? "Cash on Delivery"
                                : order.payment_status === "paid"
                                  ? "Paid · Razorpay"
                                  : "Pending payment"}
                        </div>
                    </div>
                </div>

                <div
                    className="hairline"
                    style={{
                        background: "var(--vl-border)",
                        height: 1,
                    }}
                />

                <ul className="mt-6 space-y-3 text-sm">
                    {order.items.map((i) => (
                        <li
                            key={i.product_id}
                            className="flex justify-between"
                        >
                            <span style={{ color: "var(--vl-text)" }}>
                                {i.name} × {i.quantity}
                            </span>
                            <span style={{ color: "var(--vl-muted)" }}>
                                {formatINR(i.price * i.quantity)}
                            </span>
                        </li>
                    ))}
                </ul>

                <div
                    className="hairline my-6"
                    style={{
                        background: "var(--vl-border)",
                        height: 1,
                    }}
                />
                <div className="flex justify-between items-baseline">
                    <span className="overline">Total</span>
                    <span
                        className="font-serif text-3xl"
                        style={{ color: "var(--vl-text)" }}
                    >
                        {formatINR(order.total)}
                    </span>
                </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
                <Link
                    to={`/track-order?number=${order.order_number}`}
                    className="vl-btn vl-btn-solid"
                    data-testid="order-track-btn"
                >
                    Track this order
                </Link>
                <Link to="/shop" className="vl-btn vl-btn-ghost">
                    Continue shopping
                </Link>
            </div>
        </div>
    );
}
